/**
 * Die Bauteile der Albumseite: Fotoabzug, Überschrift mit Serife, Mono-Beiwerk
 * und die Filterpille.
 *
 * Sie stehen zusammen in einer Datei, weil sie eine Formsprache sind und nicht
 * fünf unabhängige Bausteine: ein Abzug ohne die Mono-Jahreszahl darunter
 * sieht aus wie ein Bild mit weißem Rand, und eine Serifenüberschrift über
 * einer Tabelle sieht aus wie ein Versehen. Wer eins davon benutzt, braucht
 * meist auch die anderen.
 */
import { cn } from '@/lib/utils';

// ── Fotoabzug ───────────────────────────────────────────────────────────────

/**
 * Ein Bild als Abzug: weißes Papier ringsum, unten etwas mehr, und darunter
 * Platz für die Bildunterschrift.
 *
 * Warum überhaupt ein Rand? Weil ein randlos beschnittenes Foto eine Kachel
 * ist und ein Abzug ein Gegenstand. Das Archiv besteht aus Gegenständen, die
 * jemand in der Hand gehalten hat — der Rand ist die einzige Stelle, an der
 * das noch zu sehen ist.
 *
 * `size` regelt nur die Randbreite. Die Bildgröße bestimmt das Kind (meist
 * über `aspect-*`), nicht der Rahmen: sonst müsste jede Kachelbreite hier
 * eingetragen werden.
 */
export function PhotoFrame({
  children,
  caption,
  size = 'md',
  tilt,
  taped,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  /** Bildunterschrift unter dem Abzug – Serife, wie handschriftlich. */
  caption?: React.ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Leicht schief gelegt. Nur für einzelne Aufmacher, nie im Raster. */
  tilt?: boolean;
  /**
   * Mit zwei Streifen Klebefilm über den oberen Ecken – wie ins Album geklebt.
   *
   * Wie `tilt` nur für einzeln stehende Abzüge: im Raster wäre es ein Muster.
   */
  taped?: boolean;
}) {
  return (
    <div
      className={cn(
        'fb-photo',
        size === 'lg' && 'fb-photo-lg [--photo-foot:26px] [--photo-pad:10px]',
        size === 'md' && '[--photo-foot:10px] [--photo-pad:8px]',
        size === 'sm' && '[--photo-foot:8px] [--photo-pad:6px]',
        size === 'xs' && '[--photo-foot:4px] [--photo-pad:4px]',
        // Ohne Unterschrift braucht der Fuß nicht breiter zu sein als die
        // Seiten – sonst sitzt das Bild sichtbar zu hoch im Rahmen.
        !caption && size !== 'lg' && '[--photo-foot:var(--photo-pad)]',
        tilt && 'fb-tilt',
        taped && 'fb-taped',
        className,
      )}
      {...props}
    >
      {children}
      {/* Am großen Abzug schreibt eine größere Hand: 24px statt 17px. Der
          Polaroid-Aufmacher ist ein Gegenstand in Handgröße, und die Notiz
          darunter ist das Erste, was man an ihm liest – in der Größe des
          Fließtexts wirkte sie wie eine Fußnote zum Bild statt wie das, was
          jemand damals mit dem Stift daraufgeschrieben hat. Im Raster und in
          den kleinen Kacheln bleibt es bei 17px. */}
      {caption ? (
        <PhotoCaption className={size === 'lg' ? 'mt-2 text-[24px]' : undefined}>
          {caption}
        </PhotoCaption>
      ) : null}
    </div>
  );
}

/**
 * Bildunterschrift: mit der Hand geschrieben, in Kugelschreiberblau.
 *
 * Die eine Stelle in dieser Oberfläche, an der nicht das Archiv spricht,
 * sondern jemand, der den Abzug in der Hand hatte. „Werkstatt Kern, 1938 –
 * Josef, zweiter von links“ ist keine Bildbeschreibung, es ist eine Notiz –
 * und Notizen stehen auf der Rückseite von Fotos in blauer Tinte, nicht in
 * grauer Serife.
 *
 * Größer als der Fließtext, weil Schreibschriften eine kleine x-Höhe haben:
 * bei 12,5px wäre die Zeile eine Kritzelei. 17px ist hier die *gleiche*
 * gefühlte Größe.
 */
export function PhotoCaption({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <p
      className={cn(
        'mt-1.5 px-0.5 font-hand text-[17px] leading-[1.25] font-medium',
        'text-[var(--photo-pen)]',
        className,
      )}
    >
      {children}
    </p>
  );
}

/**
 * Die Fläche, wenn es kein Bild gibt – schraffiert statt grau.
 *
 * Steht auch für „wird noch verarbeitet“: dort ist das Bild da, nur noch nicht
 * sichtbar, und beides ist dieselbe Aussage – hier ist gerade nichts zu sehen.
 */
export function BlankPhoto({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={cn('fb-blank flex items-center justify-center text-ink-3', className)}
      aria-hidden={children ? undefined : true}
    >
      {children}
    </div>
  );
}

/**
 * Die Bildfläche im Abzug – das Bild, oder die Schraffur, wenn keines da ist.
 *
 * Das Seitenverhältnis kommt über `className` (`aspect-square`, `aspect-4/5`),
 * nicht als Prop: es hängt am Raster, in dem der Abzug steht, und nicht am
 * Bild.
 */
export function PhotoImage({
  src,
  alt,
  focus,
  className,
}: {
  src?: string | null;
  alt: string;
  /**
   * Wo die Gesichter sind (0…1) – siehe `MediaSummary.focus`.
   *
   * Ein Abzug ist 4:3 oder quadratisch, das Foto selten. `object-cover`
   * beschneidet die überstehende Seite und nimmt dafür ohne Zutun die *Mitte*
   * des Bildes – und die Mitte eines Familienfotos ist oft ein Tisch, während
   * die Köpfe oben herausfallen.
   *
   * `object-position` in Prozent legt den gleichnamigen Punkt des Bildes auf
   * den gleichnamigen Punkt des Ausschnitts. Die Gesichter behalten damit ihre
   * Lage im Bild („oberes Drittel bleibt oberes Drittel“) und können nicht
   * mehr herausfallen – anders als beim Beschnitt zur Mitte hin.
   */
  focus?: { x: number; y: number } | null;
  className?: string;
}) {
  return src ? (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      draggable={false}
      style={
        focus
          ? { objectPosition: `${(focus.x * 100).toFixed(1)}% ${(focus.y * 100).toFixed(1)}%` }
          : undefined
      }
      className={cn('block w-full object-cover', className)}
    />
  ) : (
    <BlankPhoto className={cn('w-full', className)} />
  );
}

// ── Schrift ─────────────────────────────────────────────────────────────────

/**
 * Kleine Versalzeile über einer Überschrift: „Generation −1 · Großvater“,
 * „Im Archiv“, „Ergebnis“. Sie ordnet ein, was danach kommt, und ist deshalb
 * nie selbst die Überschrift.
 */
export function Eyebrow({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className={cn(
        'text-[11.5px] font-semibold tracking-[0.07em] text-ink-4 uppercase',
        className,
      )}
      {...props}
    >
      {children}
    </p>
  );
}

/**
 * Jahreszahlen, Zähler, Dateinamen, Maße.
 *
 * Mono ist hier keine Typografie-Laune: Lebensdaten stehen in Rastern und
 * Listen untereinander, und in einer Proportionalschrift tanzen „1901–1978“
 * und „um 1890“ gegeneinander. Außerdem klingt Mono nach Notiz und nicht nach
 * Text – genau das ist der Rang dieser Angaben.
 */
export function Mono({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className={cn('font-mono text-[11px] text-ink-4 tabular', className)} {...props}>
      {children}
    </span>
  );
}

// ── Dunkle Tafel ────────────────────────────────────────────────────────────

/**
 * Die dunkle Tafel: „Wer ist das?“, „Wo du helfen kannst“, die Wurzel im
 * Stammbaum, offene Fragen zu einer Person.
 *
 * Sie ist im Entwurf die einzige Umkehrung, und sie ist keine Verzierung: auf
 * Papier wird gelesen, auf der Tafel wird gehandelt. Deshalb steht dort auch
 * immer genau eine Hauptaktion, und die ist gold.
 *
 * Mehr als eine Tafel pro Bildschirm nimmt ihnen den Rang.
 */
export function InkPanel({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        // `fb-panel` belegt Tinte, Linien, Flächen und die Markenfarbe für
        // alles um, was *auf* der Tafel steht (siehe index.css). Ohne das
        // müsste jedes Bauteil darin wissen, dass es auf dunklem Grund liegt –
        // und „Ist das Anna Wagner?" stand in Tinte auf Tinte.
        'fb-panel rounded-[18px] bg-panel p-4.5 sm:p-5',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/** Trennlinie *innerhalb* einer Tafel – heller Faden, nicht `--line`. */
export function PanelRow({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex items-center gap-3 border-t border-panel-line py-3', className)}
      {...props}
    >
      {children}
    </div>
  );
}

// ── Filterpille ─────────────────────────────────────────────────────────────

export type PillTone = 'neutral' | 'gold' | 'green';

/**
 * Ein Filter als Pille statt als Auswahlfeld.
 *
 * Der Unterschied ist nicht Kosmetik: ein Auswahlfeld verbirgt seinen
 * Zustand, bis man es aufklappt, und mit vier Feldern nebeneinander weiß
 * niemand mehr, was gerade eingestellt ist. Pillen zeigen die Auswahl und
 * ihre Zahl gleichzeitig — „Ohne Foto 132“ ist Filter und Antwort in einem.
 *
 * `active` färbt tintenfarben, das ist die gewählte Pille. `tone` ist etwas
 * anderes: es kennzeichnet Pillen, die auf Arbeit hinweisen (gold) oder eine
 * bereits gesetzte Einschränkung tragen (grün).
 */
export function FilterPill({
  active,
  tone = 'neutral',
  count,
  icon,
  trailing,
  className,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  active?: boolean;
  tone?: PillTone;
  /** Zahl hinter der Beschriftung – leiser als diese, immer tabellarisch. */
  count?: number | null;
  icon?: React.ReactNode;
  /**
   * Zeichen am Ende, nach der Zahl.
   *
   * Gedacht für das Kreuz an einem gesetzten Filter: die Pille schaltet ihn
   * ohnehin ab, das Kreuz sagt nur, dass hier etwas *wegzunehmen* ist. Deshalb
   * ein Zeichen und kein zweiter Knopf – ein Knopf im Knopf ist kein gültiges
   * HTML.
   */
  trailing?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={cn(
        // Dieselbe Stufe wie `size="sm"` am Knopf – in einer Filterzeile
        // stehen beide nebeneinander (siehe die Tabelle in `button.tsx`).
        'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-[13px] whitespace-nowrap',
        'transition-[background-color,border-color,color] duration-150 sm:h-8.5',
        '[&_svg]:size-3.5 [&_svg]:shrink-0',
        active
          ? 'bg-brand font-medium text-brand-ink'
          : tone === 'gold'
            ? 'bg-accent-soft font-semibold text-accent-strong hover:brightness-[0.98]'
            : tone === 'green'
              ? 'bg-ok-soft font-semibold text-ok hover:brightness-[0.98]'
              : 'border border-line bg-surface text-ink-2 hover:border-line-strong hover:text-ink',
        className,
      )}
      {...props}
    >
      {icon}
      {children}
      {count !== undefined && count !== null ? (
        <span className={cn('tabular', active ? 'opacity-70' : 'text-ink-4')}>{count}</span>
      ) : null}
      {trailing}
    </button>
  );
}

/**
 * Die Zeile, in der die Pillen stehen. Sie scrollt waagerecht statt
 * umzubrechen: eine Filterzeile, die auf dem Handy drei Zeilen hoch wird,
 * schiebt den Inhalt aus dem Bild.
 */
export function PillBar({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'scroll-none -mx-4 flex items-center gap-2 overflow-x-auto px-4 sm:mx-0 sm:flex-wrap sm:px-0',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Senkrechter Strich zwischen zwei Gruppen von Pillen. */
export function PillDivider() {
  return <span aria-hidden className="h-5.5 w-px shrink-0 bg-line-strong" />;
}
